import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Pagination, Row, Spinner, Tab, Tabs } from 'react-bootstrap';

import { Supplier, SupplierManualEntry, SupplierSelectionValue } from '../types';
import { suppliersApi } from '../services/suppliers';
import {
  EMPTY_SUPPLIER_SELECTION,
  applyCnpjMask,
  formatSupplierPhoneDisplay,
  formatSupplierPhoneInput,
  getSupplierSelectionLimits,
  isValidBrazilianPhone,
  isValidCnpj,
  isValidEmail,
  isValidInternationalPhone,
  parseSupplierSelectionValue,
  pruneSupplierSelectionToKnownIds,
  serializeSupplierSelectionValue,
} from '../utils/supplierSelection';
import './SupplierFieldControl.css';

const cloneSelection = (selectionValue: SupplierSelectionValue): SupplierSelectionValue =>
  parseSupplierSelectionValue(serializeSupplierSelectionValue(selectionValue));

interface SupplierFieldControlProps {
  value?: string;
  onChange: (nextSerializedValue: string) => void;
  disabled?: boolean;
  required?: boolean;
  workflowName?: string;
  fieldOptions?: string;
}

interface SupplierQuickCreateForm {
  corporate_name: string;
  contact_name: string;
  email: string;
  phone: string;
  cnpj: string;
  trade_name: string;
  state_registration: string;
  municipal_registration: string;
  legal_nature: string;
  opening_date: string;
  registration_status: string;
  system_registration_date: string;
  notes: string;
  is_checklist: boolean;
  is_si_high: boolean;
  is_international: boolean;
}

const EMPTY_QUICK_CREATE_FORM: SupplierQuickCreateForm = {
  corporate_name: '',
  contact_name: '',
  email: '',
  phone: '',
  cnpj: '',
  trade_name: '',
  state_registration: '',
  municipal_registration: '',
  legal_nature: '',
  opening_date: '',
  registration_status: '',
  system_registration_date: '',
  notes: '',
  is_checklist: false,
  is_si_high: false,
  is_international: false,
};

const SupplierFieldControl: React.FC<SupplierFieldControlProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  workflowName,
  fieldOptions,
}) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  /** Só limpa IDs órfãos após um load bem-sucedido do catálogo (evita wipe se a API falhar). */
  const [catalogReady, setCatalogReady] = useState(false);
  const [showSelectorModal, setShowSelectorModal] = useState(false);
  const [search, setSearch] = useState('');
  const [quickCreateForm, setQuickCreateForm] = useState<SupplierQuickCreateForm>(EMPTY_QUICK_CREATE_FORM);
  const [savingQuickCreate, setSavingQuickCreate] = useState(false);
  const [activeTab, setActiveTab] = useState<'oficiais' | 'cadastro'>('oficiais');
  const [errorMessage, setErrorMessage] = useState('');
  const [selection, setSelection] = useState<SupplierSelectionValue>(EMPTY_SUPPLIER_SELECTION);
  /** Seleção temporária enquanto a modal está aberta; só confirma no botão Salvar. */
  const [draftSelection, setDraftSelection] = useState<SupplierSelectionValue>(EMPTY_SUPPLIER_SELECTION);
  const [showInlineValidation, setShowInlineValidation] = useState(false);
  const [showCompleteFields, setShowCompleteFields] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const selectionLimits = useMemo(
    () => getSupplierSelectionLimits(workflowName, fieldOptions),
    [workflowName, fieldOptions]
  );
  const PAGE_SIZE = 10;

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      // Usar o teto da API para não podar seleção de ativos fora da primeira página.
      const response = await suppliersApi.list({ limit: 1000 });
      setSuppliers(response.items || []);
      setCatalogReady(true);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      // Não marca catalogReady: sem catálogo confiável, não remove IDs da seleção.
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  useEffect(() => {
    setSelection(parseSupplierSelectionValue(value));
  }, [value]);

  useEffect(() => {
    void loadSuppliers();
  }, []);

  // Remove da seleção IDs de fornecedores excluídos/inativos (não vêm no catálogo ativo).
  useEffect(() => {
    if (!catalogReady) return;
    const activeIds = suppliers.map((supplier) => supplier.id);
    const current = parseSupplierSelectionValue(value);
    const { selection: pruned, removedIds } = pruneSupplierSelectionToKnownIds(current, activeIds);
    if (removedIds.length === 0) return;
    setSelection(pruned);
    onChange(serializeSupplierSelectionValue(pruned));
    setDraftSelection((previousDraft) => pruneSupplierSelectionToKnownIds(previousDraft, activeIds).selection);
    // onChange estável o suficiente para o parent; evitar deps em selection para não loopar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogReady, suppliers, value]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const updateSelection = (nextSelection: SupplierSelectionValue) => {
    setSelection(nextSelection);
    onChange(serializeSupplierSelectionValue(nextSelection));
  };

  const openSelectorModal = (tab: 'oficiais' | 'cadastro' = 'oficiais') => {
    setDraftSelection(cloneSelection(selection));
    setErrorMessage('');
    setActiveTab(tab);
    setShowSelectorModal(true);
  };

  const closeSelectorModal = () => {
    if (savingQuickCreate) return;
    setShowSelectorModal(false);
    setErrorMessage('');
    setDraftSelection(cloneSelection(selection));
  };

  const handleSaveSelectedSuppliers = () => {
    const selectedCount = (draftSelection.selected_supplier_ids || []).filter(
      (id) => Number.isInteger(id) && id > 0
    ).length;
    if (selectedCount < 1) return;
    updateSelection(cloneSelection(draftSelection));
    setShowSelectorModal(false);
    setErrorMessage('');
  };

  const selectedSupplierIds = useMemo(
    () => new Set((selection.selected_supplier_ids || []).filter((id) => Number.isInteger(id) && id > 0)),
    [selection.selected_supplier_ids]
  );

  const draftSelectedSupplierIds = useMemo(
    () =>
      new Set((draftSelection.selected_supplier_ids || []).filter((id) => Number.isInteger(id) && id > 0)),
    [draftSelection.selected_supplier_ids]
  );

  const filteredSuppliers = useMemo(() => {
    const normalizedSearch = debouncedSearch.trim().toLowerCase();
    if (!normalizedSearch) return suppliers;
    const searchDigits = normalizedSearch.replace(/\D/g, '');
    const shouldSearchByCnpj = searchDigits.length >= 8;
    return suppliers.filter((supplier) => {
      const cnpjDigits = String(supplier.cnpj || '').replace(/\D/g, '');
      return (
        String(supplier.corporate_name || '').toLowerCase().includes(normalizedSearch) ||
        String(supplier.trade_name || '').toLowerCase().includes(normalizedSearch) ||
        String(supplier.contact_name || '').toLowerCase().includes(normalizedSearch) ||
        String(supplier.email || '').toLowerCase().includes(normalizedSearch) ||
        (shouldSearchByCnpj && cnpjDigits.includes(searchDigits))
      );
    });
  }, [debouncedSearch, suppliers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, showSelectorModal]);

  const totalPages = Math.max(1, Math.ceil(filteredSuppliers.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * PAGE_SIZE;
  const paginatedSuppliers = filteredSuppliers.slice(pageStart, pageStart + PAGE_SIZE);
  const visiblePages = useMemo(() => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    let start = Math.max(1, safeCurrentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [safeCurrentPage, totalPages]);

  const toggleSupplier = (supplierId: number) => {
    setShowInlineValidation(true);
    const nextSelected = new Set(draftSelectedSupplierIds);
    if (nextSelected.has(supplierId)) {
      nextSelected.delete(supplierId);
    } else {
      if (selectionLimits.max !== null && nextSelected.size >= selectionLimits.max) {
        setErrorMessage(`Você pode selecionar no máximo ${selectionLimits.max} fornecedor(es) neste tipo de fluxo.`);
        return;
      }
      nextSelected.add(supplierId);
    }
    setErrorMessage('');
    setDraftSelection({
      ...draftSelection,
      selected_supplier_ids: Array.from(nextSelected),
    });
  };

  const validateQuickCreateForm = (): string | null => {
    const corporateName = quickCreateForm.corporate_name.trim();
    const contactName = quickCreateForm.contact_name.trim();
    const email = quickCreateForm.email.trim();
    if (!corporateName) return 'Razão social é obrigatória.';
    if (!contactName) return 'Nome do contato é obrigatório.';
    if (!email) return 'E-mail é obrigatório.';
    if (!isValidEmail(email)) return 'E-mail inválido.';
    if (quickCreateForm.phone) {
      if (quickCreateForm.is_international && !isValidInternationalPhone(quickCreateForm.phone)) {
        return 'Telefone internacional deve ter entre 8 e 15 dígitos (com DDI).';
      }
      if (!quickCreateForm.is_international && !isValidBrazilianPhone(quickCreateForm.phone)) {
        return 'Telefone deve ter 10 ou 11 dígitos (DDD + número), ou 12/13 com DDI 55.';
      }
    }
    if (!quickCreateForm.is_international) {
      if (!quickCreateForm.cnpj.trim()) {
        return 'CNPJ é obrigatório.';
      }
      if (!isValidCnpj(quickCreateForm.cnpj)) {
        return 'CNPJ inválido.';
      }
    }
    return null;
  };

  const handleQuickCreateSupplier = async () => {
    setShowInlineValidation(true);
    const validationError = validateQuickCreateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setSavingQuickCreate(true);
      setErrorMessage('');

      const isInternational = !!quickCreateForm.is_international;
      const createdSupplier = showCompleteFields
        ? await suppliersApi.create({
            corporate_name: quickCreateForm.corporate_name.trim(),
            trade_name: quickCreateForm.trade_name.trim() || undefined,
            contact_name: quickCreateForm.contact_name.trim(),
            email: quickCreateForm.email.trim(),
            phone: quickCreateForm.phone ? quickCreateForm.phone.trim() : undefined,
            cnpj: isInternational ? undefined : quickCreateForm.cnpj.trim(),
            state_registration: isInternational
              ? undefined
              : quickCreateForm.state_registration.trim() || undefined,
            municipal_registration: isInternational
              ? undefined
              : quickCreateForm.municipal_registration.trim() || undefined,
            legal_nature: isInternational
              ? undefined
              : quickCreateForm.legal_nature.trim() || undefined,
            opening_date: isInternational
              ? undefined
              : quickCreateForm.opening_date.trim() || undefined,
            registration_status: isInternational
              ? undefined
              : quickCreateForm.registration_status.trim() || undefined,
            system_registration_date: quickCreateForm.system_registration_date.trim() || undefined,
            notes: quickCreateForm.notes.trim() || undefined,
            is_checklist: !!quickCreateForm.is_checklist,
            is_si_high: !!quickCreateForm.is_si_high,
            is_international: isInternational,
          })
        : await suppliersApi.quickCreate({
            corporate_name: quickCreateForm.corporate_name.trim(),
            contact_name: quickCreateForm.contact_name.trim(),
            email: quickCreateForm.email.trim(),
            phone: quickCreateForm.phone ? quickCreateForm.phone.trim() : undefined,
            cnpj: isInternational ? undefined : quickCreateForm.cnpj.trim(),
            is_checklist: !!quickCreateForm.is_checklist,
            is_si_high: !!quickCreateForm.is_si_high,
            is_international: isInternational,
          });

      setSuppliers((previous) => {
        const withoutDuplicated = previous.filter((supplier) => supplier.id !== createdSupplier.id);
        return [...withoutDuplicated, createdSupplier].sort((a, b) => a.corporate_name.localeCompare(b.corporate_name));
      });

      setDraftSelection((previousDraft) => {
        const nextManualSuppliers: SupplierManualEntry[] = [
          ...(previousDraft.manual_suppliers || []),
          {
            corporate_name: createdSupplier.corporate_name,
            contact_name: createdSupplier.contact_name,
            email: createdSupplier.email,
            phone: createdSupplier.phone ?? undefined,
            cnpj: createdSupplier.cnpj ?? undefined,
            created_supplier_id: createdSupplier.id,
          },
        ];
        const nextDraftSelection: SupplierSelectionValue = {
          ...previousDraft,
          selected_supplier_ids: Array.from(
            new Set([...(previousDraft.selected_supplier_ids || []), createdSupplier.id])
          ),
          manual_suppliers: nextManualSuppliers,
        };
        // Confirma cadastro rápido na seleção da requisição imediatamente.
        updateSelection(nextDraftSelection);
        return nextDraftSelection;
      });

      setQuickCreateForm(EMPTY_QUICK_CREATE_FORM);
      setActiveTab('oficiais');
    } catch (error: any) {
      console.error('Erro ao cadastrar fornecedor rápido:', error);
      setErrorMessage(
        error?.response?.data?.detail || 'Não foi possível cadastrar o fornecedor no momento.'
      );
    } finally {
      setSavingQuickCreate(false);
    }
  };

  const handleQuickCreateKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (target instanceof HTMLTextAreaElement) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const selectedSuppliers = suppliers.filter((supplier) => selectedSupplierIds.has(supplier.id));
  const selectedNames = selectedSuppliers.map((supplier) => supplier.corporate_name).join(', ');

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        <Form.Control
          type="text"
          readOnly
          value={selectedNames}
          placeholder="Nenhum fornecedor selecionado"
          disabled
        />
        <Button
          type="button"
          variant="outline-primary"
          onClick={() => openSelectorModal('oficiais')}
          disabled={disabled}
        >
          Selecionar fornecedores
        </Button>
        <Button
          type="button"
          variant="outline-secondary"
          onClick={() => openSelectorModal('cadastro')}
          disabled={disabled}
        >
          Cadastrar fornecedor
        </Button>
      </div>

      {showInlineValidation && required && selectedSupplierIds.size === 0 && (
        <Alert variant="warning" className="py-2">
          Selecione ao menos um fornecedor para este campo obrigatório.
        </Alert>
      )}
      {showInlineValidation && selectionLimits.max !== null && selectedSupplierIds.size > selectionLimits.max && (
        <Alert variant="warning" className="py-2">
          Este fluxo permite no máximo {selectionLimits.max} fornecedor(es).
        </Alert>
      )}
      {showInlineValidation && selectedSupplierIds.size < selectionLimits.min && (
        <Alert variant="warning" className="py-2">
          Este fluxo exige pelo menos {selectionLimits.min} fornecedor(es).
        </Alert>
      )}
      <Form.Text className="text-muted d-block">
        {selectionLimits.max === null
          ? `Regra de seleção: mínimo ${selectionLimits.min} e sem limite máximo (${selectionLimits.modeLabel}).`
          : `Regra de seleção: mínimo ${selectionLimits.min} e máximo ${selectionLimits.max} (${selectionLimits.modeLabel}).`}
      </Form.Text>

      {selectedSuppliers.length > 0 && (
        <div className="mt-2 d-flex flex-wrap gap-2">
          {selectedSuppliers.map((supplier) => (
            <Badge key={supplier.id} bg="secondary" pill>
              {supplier.corporate_name}
            </Badge>
          ))}
        </div>
      )}

      <Modal
        show={showSelectorModal}
        onHide={closeSelectorModal}
        size="lg"
        enforceFocus={false}
        restoreFocus={false}
        className="supplier-selector-nested-modal"
        backdropClassName="supplier-selector-nested-backdrop"
        dialogClassName="supplier-selector-nested-dialog"
      >
        <Modal.Header closeButton={!savingQuickCreate}>
          <Modal.Title>Fornecedores da requisição</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab((k as 'oficiais' | 'cadastro') || 'oficiais')}
            className="mb-3"
          >
            <Tab eventKey="oficiais" title="Fornecedores">
              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  type="text"
                  placeholder="Buscar fornecedor por razão social, contato, e-mail ou CNPJ"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  disabled={disabled}
                />
                <Button type="button" variant="outline-secondary" onClick={() => void loadSuppliers()} disabled={loadingSuppliers}>
                  Atualizar
                </Button>
              </div>

              {loadingSuppliers ? (
                <div className="text-muted d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  Carregando fornecedores...
                </div>
              ) : (
                <div className="border rounded p-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {filteredSuppliers.length === 0 ? (
                    <div className="text-muted">Nenhum fornecedor encontrado.</div>
                  ) : (
                    paginatedSuppliers.map((supplier) => (
                      <div key={supplier.id} className="mb-2 pb-2 border-bottom">
                        <Form.Check
                          type="checkbox"
                          id={`supplier-check-${supplier.id}`}
                          checked={draftSelectedSupplierIds.has(supplier.id)}
                          onChange={() => toggleSupplier(supplier.id)}
                          disabled={disabled}
                          label={
                            <div>
                              <strong>{supplier.corporate_name}</strong>
                              {supplier.is_international ? (
                                <Badge bg="secondary" className="ms-2">Internacional</Badge>
                              ) : null}
                              <div className="small text-muted">{supplier.contact_name || '-'} - {supplier.email || '-'}</div>
                              <div className="small text-muted">
                                {supplier.is_international
                                  ? 'Fornecedor internacional'
                                  : supplier.cnpj
                                    ? `CNPJ: ${supplier.cnpj}`
                                    : 'CNPJ não informado'}
                                {supplier.phone
                                  ? ` | Telefone: ${formatSupplierPhoneDisplay(
                                      supplier.phone,
                                      supplier.is_international,
                                    )}`
                                  : ''}
                              </div>
                            </div>
                          }
                        />
                      </div>
                    ))
                  )}
                </div>
              )}
              {filteredSuppliers.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                  <small className="text-muted">
                    Exibindo {pageStart + 1}-
                    {Math.min(pageStart + PAGE_SIZE, filteredSuppliers.length)} de {filteredSuppliers.length}
                  </small>
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={safeCurrentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={safeCurrentPage === 1}
                    />
                    {visiblePages.map((page) => (
                      <Pagination.Item key={page} active={page === safeCurrentPage} onClick={() => setCurrentPage(page)}>
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={safeCurrentPage >= totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={safeCurrentPage >= totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </Tab>

            <Tab eventKey="cadastro" title="Cadastro rápido">
              <div onKeyDown={handleQuickCreateKeyDown}>
                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                <Form.Check
                  type="switch"
                  id="supplier-complete-mode"
                  label="Preencher cadastro completo"
                  checked={showCompleteFields}
                  onChange={(event) => setShowCompleteFields(event.target.checked)}
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="supplier-international-quick"
                  label="Fornecedor internacional (sem CNPJ)"
                  checked={quickCreateForm.is_international}
                  onChange={(event) =>
                    setQuickCreateForm((prev) => {
                      const isInternational = event.target.checked;
                      return {
                        ...prev,
                        is_international: isInternational,
                        phone: prev.phone
                          ? formatSupplierPhoneInput(prev.phone, isInternational)
                          : prev.phone,
                        ...(isInternational
                          ? {
                              cnpj: '',
                              state_registration: '',
                              municipal_registration: '',
                              legal_nature: '',
                              opening_date: '',
                              registration_status: '',
                            }
                          : {}),
                      };
                    })
                  }
                  className="mb-3"
                />
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Label>Razão social *</Form.Label>
                    <Form.Control
                      value={quickCreateForm.corporate_name}
                      onChange={(event) => setQuickCreateForm((prev) => ({ ...prev, corporate_name: event.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Nome do contato *</Form.Label>
                    <Form.Control
                      value={quickCreateForm.contact_name}
                      onChange={(event) => setQuickCreateForm((prev) => ({ ...prev, contact_name: event.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>E-mail *</Form.Label>
                    <Form.Control
                      type="email"
                      value={quickCreateForm.email}
                      onChange={(event) => setQuickCreateForm((prev) => ({ ...prev, email: event.target.value }))}
                      required
                    />
                  </Col>
                  <Col md={quickCreateForm.is_international ? 12 : 6}>
                    <Form.Label>
                      {quickCreateForm.is_international
                        ? 'Telefone internacional (com DDI)'
                        : 'Telefone'}
                    </Form.Label>
                    <Form.Control
                      value={quickCreateForm.phone}
                      onChange={(event) =>
                        setQuickCreateForm((prev) => ({
                          ...prev,
                          phone: formatSupplierPhoneInput(
                            event.target.value,
                            !!prev.is_international,
                          ),
                        }))
                      }
                      maxLength={quickCreateForm.is_international ? 25 : undefined}
                      placeholder={
                        quickCreateForm.is_international
                          ? '+1 415 555 0100'
                          : '(00) 00000-0000 ou +55 (00) 00000-0000'
                      }
                    />
                  </Col>
                  {!quickCreateForm.is_international && (
                    <Col md={6}>
                      <Form.Label>CNPJ *</Form.Label>
                      <Form.Control
                        value={quickCreateForm.cnpj}
                        onChange={(event) =>
                          setQuickCreateForm((prev) => ({ ...prev, cnpj: applyCnpjMask(event.target.value) }))
                        }
                        placeholder="00.000.000/0000-00"
                        required
                      />
                    </Col>
                  )}
                  {showCompleteFields && (
                    <>
                      <Col md={12}>
                        <Form.Label>Nome fantasia</Form.Label>
                        <Form.Control
                          value={quickCreateForm.trade_name}
                          onChange={(event) =>
                            setQuickCreateForm((prev) => ({ ...prev, trade_name: event.target.value }))
                          }
                        />
                      </Col>
                      {!quickCreateForm.is_international && (
                        <>
                          <Col md={6}>
                            <Form.Label>Inscrição estadual</Form.Label>
                            <Form.Control
                              value={quickCreateForm.state_registration}
                              onChange={(event) =>
                                setQuickCreateForm((prev) => ({
                                  ...prev,
                                  state_registration: event.target.value,
                                }))
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Inscrição municipal</Form.Label>
                            <Form.Control
                              value={quickCreateForm.municipal_registration}
                              onChange={(event) =>
                                setQuickCreateForm((prev) => ({
                                  ...prev,
                                  municipal_registration: event.target.value,
                                }))
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Natureza jurídica</Form.Label>
                            <Form.Control
                              value={quickCreateForm.legal_nature}
                              onChange={(event) =>
                                setQuickCreateForm((prev) => ({ ...prev, legal_nature: event.target.value }))
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Data de abertura</Form.Label>
                            <Form.Control
                              type="date"
                              value={quickCreateForm.opening_date}
                              onChange={(event) =>
                                setQuickCreateForm((prev) => ({ ...prev, opening_date: event.target.value }))
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Situação cadastral</Form.Label>
                            <Form.Control
                              value={quickCreateForm.registration_status}
                              onChange={(event) =>
                                setQuickCreateForm((prev) => ({
                                  ...prev,
                                  registration_status: event.target.value,
                                }))
                              }
                            />
                          </Col>
                        </>
                      )}
                      <Col md={6}>
                        <Form.Label>Data cadastro no sistema</Form.Label>
                        <Form.Control
                          type="date"
                          value={quickCreateForm.system_registration_date}
                          onChange={(event) =>
                            setQuickCreateForm((prev) => ({
                              ...prev,
                              system_registration_date: event.target.value,
                            }))
                          }
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label>Observações gerais</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={quickCreateForm.notes}
                          onChange={(event) =>
                            setQuickCreateForm((prev) => ({ ...prev, notes: event.target.value }))
                          }
                        />
                      </Col>
                    </>
                  )}
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="quick-supplier-checklist"
                      label="Fornecedor com checklist"
                      checked={!!quickCreateForm.is_checklist}
                      onChange={(event) =>
                        setQuickCreateForm((prev) => ({ ...prev, is_checklist: event.target.checked }))
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="quick-supplier-si-high"
                      label="Fornecedor SI Alto"
                      checked={!!quickCreateForm.is_si_high}
                      onChange={(event) =>
                        setQuickCreateForm((prev) => ({ ...prev, is_si_high: event.target.checked }))
                      }
                    />
                  </Col>
                  <Col md={12} className="d-flex justify-content-end">
                    <Button
                      type="button"
                      variant="primary"
                      disabled={savingQuickCreate}
                      onClick={() => void handleQuickCreateSupplier()}
                    >
                      {savingQuickCreate ? 'Salvando...' : (showCompleteFields ? 'Salvar cadastro completo' : 'Salvar fornecedor')}
                    </Button>
                  </Col>
                </Row>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between flex-wrap gap-2">
          <div className="text-muted small">
            {draftSelectedSupplierIds.size > 0
              ? `${draftSelectedSupplierIds.size} fornecedor(es) selecionado(s)`
              : 'Nenhum fornecedor selecionado'}
          </div>
          <div className="d-flex gap-2">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={closeSelectorModal}
              disabled={savingQuickCreate}
            >
              Fechar
            </Button>
            {draftSelectedSupplierIds.size >= 1 && (
              <Button
                type="button"
                variant="primary"
                onClick={handleSaveSelectedSuppliers}
                disabled={savingQuickCreate || disabled}
              >
                Confirmar e fechar
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SupplierFieldControl;
